import React, { useState } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { Helmet } from "react-helmet";
import {
    useGlobalLocalization,
    usePageLocalization,
} from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { ProductReviewsModel } from "../models/productReviewsModel";

interface ISeo {
    title: string;
    description?: string;
    isDescriptionCurrentLocale?: boolean;
    type?: string;
    schema?: { product: any; review: ProductReviewsModel | null };
    meta?: any[];
}

const DEFAULT_AGGREGATE_RATING =
    '"aggregateRating": { "@type": "AggregateRating", "ratingValue": 5, "reviewCount": 1 }';

function SEO({ title, description = "", isDescriptionCurrentLocale, schema, meta = [] }: ISeo) {
    const { currentLocale } = usePageLocalization();
    const { site } = useStaticQuery(
        graphql`
            query {
                site {
                    siteMetadata {
                        title
                        description
                        siteUrl
                    }
                }
            }
        `
    );

    const isDev =
        process.env.GATSBY_BUILD_TYPE === "dev" || process.env.GATSBY_BUILD_TYPE === "staging";

    if (isDescriptionCurrentLocale && currentLocale.includes("en_")) {
        description = `${description}${currentLocale === "en_CA" ? " Can." : " Us."}`;
    }

    const [aggregateRating, setAggregateRating] = useState(DEFAULT_AGGREGATE_RATING);

    let { localizationContext } = useGlobalLocalization();
    let { currentPageAlternatives } = usePageLocalization();

    /* Schema related */
    const { product, review } = schema || {};

    if (review && aggregateRating === "") {
        setAggregateRating(`"aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": ${review.average},
            "reviewCount": ${review.count}
        }`);
    }

    const imageProductInternalArray =
        product?.fields?.images?.length > 0
            ? product.fields?.images
                  .slice(0, 1)
                  .map(
                      (image: any) =>
                          `"https://www.century-heating.com${image?.childImageSharp?.original?.src}"`
                  )
            : [];

    return (
        <Helmet
            titleTemplate={`%s${
                !title.includes(site.siteMetadata.title) ? ` | ${site.siteMetadata.title}` : ''
            }`}
        >
            <html lang={localizationContext.currentLocale.bcp47LocaleCode} />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:type" content="website" />
            <meta property="twitter:card" content="summary" />
            {isDev && <meta name="robots" content="noindex, nofollow" />}
            <meta property="twitter:creator" content={site.siteMetadata.author} />
            <meta property="twitter:title" content={title} />
            <meta property="twitter:description" content={description} />

            <link
                rel="canonical"
                href={
                    site.siteMetadata.siteUrl +
                    currentPageAlternatives[localizationContext.currentLocale.localeCode]
                }
            />
            {Object.keys(currentPageAlternatives).map((key) => (
                <link
                    rel="alternate"
                    href={site.siteMetadata.siteUrl + currentPageAlternatives[key]}
                    hrefLang={localizationContext.locales[key].bcp47LocaleCode.toLowerCase()}
                    key={key.toLowerCase()}
                />
            ))}

            {schema && (
                <script type="application/ld+json">
                    {`{
                        "@context": "https://schema.org/",
                        "@type": "Product",
                        "image": [${
                            imageProductInternalArray.length > 0
                                ? imageProductInternalArray.map((image: string) => `${image}`)
                                : ""
                        }],
                        "description": "${product?.description?.[
                            localizationContext.currentLocale.localeCode
                        ]
                            .split("\n")
                            .join(",")
                            .replace(/"/gi, '\\"')}",
                        "brand": {
                            "@type": "Brand",
                            "name": "Century heating"
                        },
                        "manufacturer": "SBI - Stove Builder International Inc.",
                        "model": "${product.code}",
                        "name": "${product.title[localizationContext.currentLocale.localeCode]}",
                        "category": "${product.category}",
                        ${aggregateRating}                                       
                    }`}
                </script>
            )}
        </Helmet>
    );
}

export default SEO;
